import { LogService } from 'htv-lib'
import { Injectable } from '@angular/core'
import { IHTVPlatform } from './platform'
import { MonitoringMemoryData, MonitoringStorageData, SystemData } from '../interfaces/monitoring'
import { LocalChannel } from '../interfaces/local-channel'
import { ChannelPosition } from '../pages/channel/channel-position'
import { DVBTChannel, UDPChannel, WebChannel } from '../interfaces/channel'
import { LocalMediaFile, Media } from 'htv-lib'
import { TizenInternal } from './tizen-internal'
import { LocalAppLaunchMode, TVApp } from '../interfaces/local-app'
import { ChannelTrackData, ChannelTrackDataFlags, CurrentChannelData } from '../interfaces/channel-track-data'
import { PowerState } from '../interfaces/power-state'
import { BehaviorSubject } from 'rxjs'
import { environment } from '../environments/environment'
import { NetworkState, NetworkConnectionType, NetworkData, NetworkSoftAP, NetworkWifiData } from '../interfaces/network'
import {
  TizenAudioInfo,
  TizenAudioType,
  TizenNetworkState,
  TizenSubtitleInfo,
  TizenSubtitleMode,
  TizenSystemInfoCpu,
  TizenSystemInfoStorage,
} from './tizen-types'
import { StatusSuccessDto } from '../interfaces/dto/status-response-dto'
import { GoogleCastState } from '../interfaces/local-googlecast'
import { ExternalInputData } from '../services/external-input.service'
import { VODStatus, VODVideo } from '../interfaces/vod'

declare const b2bapis: any | undefined
declare const tizen: any | undefined
declare const webapis: any | undefined

interface ApplicationInformation {
  id: string
  name: string
  iconPath: string
  version: string
  show: boolean
  categories: string[]
  installDate: Date
  size: number
  packageId: string
}

@Injectable({
  providedIn: 'root',
})
export class TizenHBrowserApiPlatform implements IHTVPlatform {
  private tizenInternal: TizenInternal

  private readonly ROOT_DIR = 'documents'
  private readonly MEDIA_DIR = 'medias'
  private readonly MEDIA_PATH = 'documents/medias'

  isSubtitleEnabled = false

  gcInitializedSubject?: BehaviorSubject<boolean | undefined>
  channelChangedSubject?: BehaviorSubject<boolean>

  private isSeeking = false

  constructor(private logService: LogService) {
    this.logService.debug('Initialisé avec les anciennes API Tizen')
    this.tizenInternal = new TizenInternal(logService)
  }

  id(): string {
    return 'TizenPlatform'
  }

  async init(): Promise<void> {
    await this.loadScript('$WEBAPIS/webapis/webapis.js')
    await this.loadScript('$B2BAPIS/b2bapis/b2bavplay.js')
    await this.loadScript('$B2BAPIS/b2bapis/b2bapis.js')

    this.registerRemoteKeys()
    this.initStorage()
    this.registerVolumeChangeListener()
  }

  afterRegistrationHook(deviceId: string | undefined): void {
    this.setupURLLauncherAddress(deviceId)
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  afterStatusHook(status: StatusSuccessDto): Promise<void> {
    return Promise.resolve()
  }

  enableGuestMode(): Promise<void> {
    return Promise.resolve()
  }

  disableGuestMode(): Promise<void> {
    return Promise.resolve()
  }

  clearData(): Promise<void> {
    return Promise.resolve()
  }

  getSystemData(): Promise<SystemData> {
    return Promise.resolve({
      model: this.tizenInternal.getModel(),
      manufacturer: 'Samsung',
      serialNumber: this.tizenInternal.getHBrowserSerialNumber(),
      firmwareVersion: this.tizenInternal.getFirmware(),
      appVersion: this.tizenInternal.getAppVersion(),
    })
  }

  getPlatformData(): Promise<object> {
    const data: { [key: string]: any } = {}

    data['userAgent'] = navigator.userAgent
    data['appInfo'] = this.tizenInternal.getCurrentAppInfo()
    data['appVersion'] = this.tizenInternal.getAppVersion()

    try {
      data['urlLauncherAddress'] = b2bapis.b2bcontrol.getURLLauncherAddress()
    } catch (err: any) {
      this.logService.error(`(b2bapis.b2bcontrol.getURLLauncherAddress()), error = ${err?.message}`)
    }

    const productInfoData: any = {}
    productInfoData['version'] = this.tizenInternal.getProductInfoVersion()
    productInfoData['firmware'] = this.tizenInternal.getFirmware()
    productInfoData['duid'] = this.tizenInternal.getDuid()
    productInfoData['modelCode'] = this.tizenInternal.getModelCode()
    productInfoData['model'] = this.tizenInternal.getModel()
    productInfoData['smartTVServerType'] = this.tizenInternal.getSmartTVServerType()
    productInfoData['smartTVServerVersion'] = this.tizenInternal.getSmartTVServerVersion()
    productInfoData['isUdPanelSupported'] = this.tizenInternal.isUdPanelSupported()
    productInfoData['realModel'] = this.tizenInternal.getRealModel()
    productInfoData['getLocalSet'] = this.tizenInternal.getLocalSet()
    productInfoData['isUHDAModel'] = this.tizenInternal.isUHDAModel()
    productInfoData['configKeyDataService'] = this.tizenInternal.getSystemConfig(
      webapis.productinfo.ProductInfoConfigKey.CONFIG_KEY_DATA_SERVICE
    )
    productInfoData['configKeyServiceCountry'] = this.tizenInternal.getSystemConfig(
      webapis.productinfo.ProductInfoConfigKey.CONFIG_KEY_SERVICE_COUNTRY
    )
    data['productinfo'] = productInfoData

    const systemcontrolData: any = {}
    systemcontrolData['serialNumber'] = this.tizenInternal.getHBrowserSerialNumber()
    data['systemcontrol'] = systemcontrolData

    const wrapPromise = (key: string, promise: Promise<any>) => {
      return promise.then((data: any) => ({ key: key, data: data }))
    }

    const promises = [
      wrapPromise('SystemInfoDisplay', this.tizenInternal.getSystemInfoPropertyValue('DISPLAY')),
      wrapPromise('SystemInfoPanel', this.tizenInternal.getSystemInfoPropertyValue('PANEL')),
      wrapPromise('SystemInfoDeviceOrientation', this.tizenInternal.getSystemInfoPropertyValue('DEVICE_ORIENTATION')),
      wrapPromise('SystemInfoBuild', this.tizenInternal.getSystemInfoPropertyValue('BUILD')),
      wrapPromise('SystemInfoLocale', this.tizenInternal.getSystemInfoPropertyValue('LOCALE')),
      wrapPromise('SystemInfoPeripheral', this.tizenInternal.getSystemInfoPropertyValue('PERIPHERAL')),
      wrapPromise('SystemInfoVideosource', this.tizenInternal.getSystemInfoPropertyValue('VIDEOSOURCE')),
      wrapPromise('SystemInfoSourceInfo', this.tizenInternal.getSystemInfoPropertyValue('SOURCE_INFO')),
      wrapPromise('SystemInfoServiceCountry', this.tizenInternal.getSystemInfoPropertyValue('SERVICE_COUNTRY')),
    ]

    return Promise.allSettled(promises)
      .then(results => {
        for (let i = 0; i < results.length; i++) {
          const result = results[i]
          if (result.status === 'fulfilled') {
            data[result.value.key] = result.value.data
          }
        }
        return data
      })
      .catch(() => {
        return data
      })
  }

  getCpuUsage(): Promise<number> {
    return this.tizenInternal.getSystemInfoPropertyValue('CPU').then((data: TizenSystemInfoCpu) => {
      return data.load
    })
  }

  getMemoryData(): Promise<MonitoringMemoryData> {
    return Promise.resolve({
      availableMemory: this.tizenInternal.getAvailableMemory(),
      totalMemory: this.tizenInternal.getTotalMemory(),
    })
  }

  getStorageData(): Promise<MonitoringStorageData[]> {
    return this.tizenInternal.getSystemInfoPropertyValue('STORAGE').then((data: TizenSystemInfoStorage) => {
      return data.units.map(unit => ({
        type: unit.type,
        availableCapacity: unit.availableCapacity,
        capacity: unit.capacity,
        isRemovable: unit.isRemovable,
      }))
    })
  }

  getTemperature(): Promise<number> {
    return new Promise((resolve, reject) => {
      if (this.tizenInternal.hasCapability('http://tizen.org/feature/sensor.temperature')) {
        try {
          resolve(b2bapis.b2bcontrol.getCurrentTemperature())
        } catch (err: any) {
          this.logService.error(`Could not retrieve temperature, error = ${err?.message}`)
          reject()
        }
      } else {
        reject()
      }
    })
  }

  getValue(key: string): any {
    return localStorage.getItem(key)
  }

  setValue(key: string, value: any): void {
    localStorage.setItem(key, value)
  }

  removeValue(key: string): void {
    localStorage.removeItem(key)
  }

  clearValues(): void {
    localStorage.clear()
  }

  loadScript(path: string): Promise<boolean> {
    return new Promise(resolve => {
      const script = document.createElement('script')
      script.type = 'text/javascript'
      script.onload = () => {
        this.logService.debug('Loaded script ' + path)
        resolve(true)
      }
      script.onerror = (err: any) => {
        this.logService.error('Could not load script ' + path)
        this.logService.error(err)
        resolve(false)
      }
      script.src = path
      document.head.appendChild(script)
    })
  }

  registerRemoteKeys(): void {
    const supportedKeys = tizen.tvinputdevice.getSupportedKeys()
    for (const supportedKey of supportedKeys) {
      try {
        this.logService.debug('Registering Key ' + supportedKey.name)
        tizen.tvinputdevice.registerKey(supportedKey.name)
      } catch (err: any) {
        this.logService.error('Could not register key ' + supportedKey.name)
        this.logService.error(err.message)
      }
    }
    const specialKeysObj = [
      ['Netflix'],
      ['SmartHub'],
      ['Content'], // Home key
    ]
    for (const key of specialKeysObj) {
      try {
        this.logService.debug('Registering Key ' + key)
        webapis.appcommon.registerKey(key)
      } catch (err: any) {
        this.logService.error('Could not register key + ' + key)
        this.logService.error(err.message)
      }
    }
  }

  initStorage(): void {
    const successCallback = (path: string) => {
      this.logService.info(`Successfully created directory ${path}`)
    }
    const errorCallback = (err: any) => {
      this.logService.error(`Could not create directory, error = ${err.message}`)
    }

    try {
      this.pathExists(this.MEDIA_PATH, (exists: boolean) => {
        if (!exists) {
          tizen.filesystem.resolve(
            this.ROOT_DIR,
            (dir: { createDirectory: (arg0: string) => void }) => {
              try {
                dir.createDirectory(this.MEDIA_DIR)
                successCallback(this.MEDIA_PATH)
              } catch (error) {
                errorCallback(error)
              }
            },
            (err: any) => {
              errorCallback(err)
            },
            'rw'
          )
        }
      })
    } catch (err: any) {
      this.logService.error(`Could not create directory (2), error = ${err.message}`)
    }
  }

  registerVolumeChangeListener(): void {
    const currentVolume = tizen.tvaudiocontrol.getVolume()
    this.logService.debug(`Current volume: ${currentVolume}`)
    const audioOutputMode = tizen.tvaudiocontrol.getOutputMode()
    this.logService.debug(`AudioOutputMode: ${audioOutputMode}`)
    const onVolumeChanged = (volume: string) => {
      this.logService.debug(`Volume changed to ${volume}`)
    }
    tizen.tvaudiocontrol.setVolumeChangeListener(onVolumeChanged)
  }

  reboot(): void {
    const successCallback = () => {
      this.logService.info(`Successfully reboot device`)
    }
    const errorCallback = (err: any) => {
      this.logService.error(`Could not reboot device, error = ${err.message}`)
    }
    b2bapis.b2bcontrol.rebootDevice(successCallback, errorCallback)
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setLanguage(code: string): Promise<void> {
    return Promise.resolve()
  }

  getPowerState(): Promise<PowerState | undefined> {
    return new Promise(resolve => {
      try {
        const tizenPowerState = b2bapis.b2bpower.getPowerState()
        const powerState = tizenPowerState == 'Normal' ? PowerState.NORMAL : PowerState.STANDBY
        resolve(powerState)
      } catch (err: any) {
        this.logService.error(`Could not get power state, error = ${err.message}`)
        resolve(undefined)
      }
    })
  }

  setPowerState(state: PowerState): void {
    const successCallback = () => {
      this.logService.info(`Successfully set power state to ${state}`)
    }
    const errorCallback = (err: any) => {
      this.logService.error(`Could not set power state to ${state}, error = ${err.message}`)
    }
    if (state == PowerState.STANDBY) {
      b2bapis.b2bcontrol.setPowerOff(successCallback, errorCallback)
    } else {
      b2bapis.b2bpower.setPowerOn(successCallback, errorCallback)
    }
  }

  registerPowerStateListener(subject: BehaviorSubject<PowerState | undefined>): void {
    try {
      b2bapis.b2bpower.unsetPowerStateChangeListener()
    } catch (err: any) {
      this.logService.error(`Error while unregistering power state listener, error = ${err.message}`)
    }

    const listener = async () => {
      subject.next(await this.getPowerState())
    }

    try {
      b2bapis.b2bpower.setPowerStateChangeListener(listener)
    } catch (err: any) {
      this.logService.error(`Error while registering power state listener, error = ${err.message}`)
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  registerApplicationInstalledListener(subject: BehaviorSubject<string[]>): void {
    // do nothing
  }

  // Did not find an API that allows us to listen to the focus state
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  registerApplicationFocusListener(subject: BehaviorSubject<boolean>): void {
    // do nothing
  }

  getNetworkState(): Promise<NetworkState> {
    try {
      const gatewayStatus = webapis.network.isConnectedToGateway()
      const networkState = gatewayStatus ? NetworkState.CONNECTED : NetworkState.DISCONNECTED
      return Promise.resolve(networkState)
    } catch (err: any) {
      this.logService.error(`Could not retrieve isConnectedToGateway, error = ${err?.message}`)
      return Promise.resolve(NetworkState.UNKNOWN)
    }
  }

  getNetworkData(): Promise<NetworkData> {
    const connectionType = this.tizenInternal.getNetworkConnectionType()
    const networkData: NetworkData = {
      type: connectionType,
      macAddress: this.tizenInternal.getNetworkMac(),
      ipAddress: this.tizenInternal.getNetworkIp(),
      ipMode: this.tizenInternal.getNetworkIpMode(),
      subnetMask: this.tizenInternal.getNetworkSubnetMask(),
      gateway: this.tizenInternal.getNetworkGateway(),
      primaryDns: this.tizenInternal.getNetworkDns(),
      secondaryDns: this.tizenInternal.getNetworkSecondaryDns(),
      proxy: this.tizenInternal.getNetworkProxy(),
    }
    if (connectionType == NetworkConnectionType.WIFI) {
      const wifi: NetworkWifiData = {
        ssid: this.tizenInternal.getWiFiSsid(),
        signalStrengthLevel: this.tizenInternal.getWiFiSignalStrengthLevel(),
        securityMode: this.tizenInternal.getWiFiSecurityMode(),
        encryptionType: this.tizenInternal.getWiFiEncryptionType(),
      }
      networkData.wifi = wifi
    }
    if (connectionType == NetworkConnectionType.ETHERNET) {
      if (this.tizenInternal.isSoftAPEnabled()) {
        const softAP: NetworkSoftAP = {
          enabled: true,
          ssid: this.tizenInternal.getSoftAPSSID(),
          securityKey: this.tizenInternal.getSoftAPSecurityKey(),
        }
        networkData.softAP = softAP
      }
    }
    return Promise.resolve(networkData)
  }

  networkStateListenerId?: number
  networkStateRetryTimer?: ReturnType<typeof setTimeout>
  registerNetworkStateListener(subject: BehaviorSubject<NetworkState>): void {
    clearTimeout(this.networkStateRetryTimer)
    this.networkStateRetryTimer = undefined

    if (this.networkStateListenerId) {
      try {
        webapis.network.removeNetworkStateChangeListener(this.networkStateListenerId)
        this.networkStateListenerId = undefined
      } catch (err: any) {
        this.logService.error(`Error while unregistering network state listener, error = ${err.message}`)
      }
    }

    const listener = (state: TizenNetworkState) => {
      let networkState: NetworkState | undefined = undefined
      switch (state) {
        case TizenNetworkState.INTERNET_CONNECTED:
          networkState = NetworkState.CONNECTED
          break
        case TizenNetworkState.LAN_CABLE_ATTACHED:
          networkState = NetworkState.UNKNOWN
          break
        case TizenNetworkState.LAN_CABLE_DETACHED:
          networkState = NetworkState.UNKNOWN
          break
        case TizenNetworkState.LAN_CABLE_STATE_UNKNOWN:
          networkState = NetworkState.UNKNOWN
          break
        case TizenNetworkState.GATEWAY_CONNECTED:
          networkState = NetworkState.CONNECTED
          break
        case TizenNetworkState.GATEWAY_DISCONNECTED:
          networkState = NetworkState.DISCONNECTED
          break
        case TizenNetworkState.WIFI_MODULE_STATE_ATTACHED:
          networkState = NetworkState.UNKNOWN
          break
        case TizenNetworkState.WIFI_MODULE_STATE_DETACHED:
          networkState = NetworkState.UNKNOWN
          break
        case TizenNetworkState.WIFI_MODULE_STATE_UNKNOWN:
          networkState = NetworkState.UNKNOWN
          break
        case TizenNetworkState.INTERNET_DISCONNECTED:
          networkState = NetworkState.DISCONNECTED
          break
        default:
          networkState = NetworkState.UNKNOWN
          break
      }
      subject.next(networkState)
    }

    try {
      this.networkStateListenerId = webapis.network.addNetworkStateChangeListener(listener)
    } catch (err: any) {
      this.logService.error(`Error while registering network state listener, error = ${err.message}, retrying in 5s`)
      this.networkStateRetryTimer = setTimeout(() => this.registerNetworkStateListener(subject), 5000)
    }

    this.getNetworkState().then(networkState => subject.next(networkState))
  }

  getAppList(): Promise<TVApp[]> {
    return new Promise(resolve => {
      const onListInstalledApps = (appInfos: ApplicationInformation[]) => {
        resolve(
          appInfos.map(appInfo => ({
            id: appInfo.id,
            name: appInfo.name,
            iconPath: appInfo.iconPath,
            version: appInfo.version,
            internalApp: appInfo,
          }))
        )
      }
      tizen.application.getAppsInfo(onListInstalledApps)
    })
  }

  startApp(appId: string): void {
    const successCallback = () => {
      this.logService.info(`Successfully launched app ${appId}`)
    }
    const errorCallback = (err: any) => {
      this.logService.error(`Could not launch app ${appId} - error = ${err.message}`)
    }
    tizen.application.launch(appId, successCallback, errorCallback)
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  startNetflixApp(appId: string, hotelId: string, launchMode: LocalAppLaunchMode): void {
    this.startApp(appId)
  }

  registerChannelListener(subject: BehaviorSubject<boolean>): void {
    this.channelChangedSubject = subject
  }

  startUDPChannel(channel: LocalChannel, pos: ChannelPosition): void {
    const source = channel.source as UDPChannel

    if (!source.ip) {
      this.logService.error('Invalid UDP channel configuration: missing IP')
      return
    }

    if (!source.port) {
      this.logService.error('Invalid UDP channel configuration: missing port')
      return
    }

    try {
      const protocol = source.rtp ? 'rtp' : 'udp'
      const url = `${protocol}://${source.ip}:${source.port}`
      const listener = {
        onbufferingstart: () => {
          this.logService.debug('Buffering start')
        },
        onbufferingprogress: (percent: any) => {
          this.logService.debug('Buffering progress: ' + percent + '%')
        },
        onbufferingcomplete: () => {
          this.logService.debug('Buffering complete')
        },
        onstreamcompleted: () => {
          this.logService.debug('Stream completed')
          webapis.avplay.stop()
        },
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        oncurrentplaytime: (currentTime: any) => {
          //this.logService.debug('Current playtime: ' + currentTime)
        },
        onerror: (eventType: any) => {
          this.logService.error('(onerror) ' + eventType)
        },
        onerrormsg: (eventType: any, errorMsg: any) => {
          this.logService.debug(`(onerrormsg) ${eventType} ${errorMsg}`)
        },
        onevent: (eventType: any, eventData: any) => {
          this.logService.debug(`(onevent) ${eventType} data: ${JSON.stringify(eventData)}`)
        },
        onsubtitlechange: (duration: number, text: string, type: string, attributes: any) => {
          this.logService.debug(`(onsubtitlechange) ${duration} ${text} ${type} ${JSON.stringify(attributes)}`)
        },
        ondrmevent: (drmEvent: any, drmData: any) => {
          this.logService.debug(`(ondrmevent) ${drmEvent} data: ' ${JSON.stringify(drmData)}`)
        },
      }

      // Test if the URL is compatible before opening
      if (!url.startsWith('udp://') && !url.startsWith('rtp://')) {
        this.logService.error('The provided URL is not a valid UDP/RTP format: ' + url)
        return
      }

      this.logService.debug(`Loading UDP channel ${channel.name} on ${url}`)

      webapis.avplay.open(url)
      webapis.avplay.setDisplayRect(pos.left, pos.top, pos.width, pos.height)
      webapis.avplay.setListener(listener)
      webapis.avplay.prepareAsync(
        () => {
          this.logService.debug(`UDP channel ${channel.name} loaded`)
          this.setSubtitleState(false)
          webapis.avplay.play()
          this.channelChangedSubject?.next(true)
        },
        (err: any) => {
          this.logService.error(`Failed to prepare AVPlay for URL: ${url}. Error: ${err?.message}`)
          this.channelChangedSubject?.next(false)
        }
      )
    } catch (err: any) {
      this.logService.error(`Could not launch UDP channel. General error: ${err?.message}`)
      this.channelChangedSubject?.next(false)
    }
  }

  startWebChannel(channel: LocalChannel, pos: ChannelPosition): void {
    const source = channel.source as WebChannel

    if (!source.type) {
      this.logService.error(`Invalid WEB channel configuration: type = ${source.type}`)
      return
    }

    if (!source.url) {
      this.logService.error(`Invalid WEB channel configuration: url = ${source.url}`)
      return
    }

    const url = source.url
    const listener = {
      onbufferingstart: () => {
        this.logService.debug('Buffering start')
      },
      onbufferingprogress: (percent: any) => {
        this.logService.debug('Buffering progress: ' + percent + '%')
      },
      onbufferingcomplete: () => {
        this.logService.debug('Buffering complete')
      },
      onstreamcompleted: () => {
        this.logService.debug('Stream completed')
        webapis.avplay.stop()
      },
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      oncurrentplaytime: (currentTime: any) => {
        //this.logService.debug('Current playtime: ' + currentTime)
      },
      onerror: (eventType: any) => {
        this.logService.error('(onerror) ' + eventType)
      },
      onerrormsg: (eventType: any, errorMsg: any) => {
        this.logService.debug(`(onerrormsg) ${eventType} ${errorMsg}`)
      },
      onevent: (eventType: any, eventData: any) => {
        this.logService.debug(`(onevent) ${eventType} data: ${JSON.stringify(eventData)}`)
      },
      onsubtitlechange: (duration: number, text: string, type: string, attributes: any) => {
        this.logService.debug(`(onsubtitlechange) ${duration} ${text} ${type} ${JSON.stringify(attributes)}`)
      },
      ondrmevent: (drmEvent: any, drmData: any) => {
        this.logService.debug(`(ondrmevent) ${drmEvent} data: ${JSON.stringify(drmData)}`)
      },
    }

    this.logService.debug(`Loading WEB channel ${channel.name} on ${url}`)

    try {
      webapis.avplay.open(url)
      webapis.avplay.setDisplayRect(pos.left, pos.top, pos.width, pos.height)
      webapis.avplay.setListener(listener)
      webapis.avplay.prepareAsync(
        () => {
          this.logService.debug(`WEB channel ${channel.name} loaded`)
          this.setSubtitleState(false)
          webapis.avplay.play()
          this.channelChangedSubject?.next(true)
        },
        (err: any) => {
          this.logService.error(`Failed to prepare AVPlay for URL: ${url}. Error: ${err.message}`)
          this.channelChangedSubject?.next(false)
        }
      )
    } catch (err: any) {
      this.logService.error(`Could not launch WEB channel. General error: ${err?.message}`)
      this.channelChangedSubject?.next(false)
    }
  }

  async startBroadcastChannel(channel: LocalChannel, pos: ChannelPosition): Promise<void> {
    const source = channel.source as DVBTChannel
    this.logService.debug(`Starting broadcast channel ${channel.name}`)
    try {
      const videoSource = await this.tizenInternal.getVideoSource('TV')
      await this.tizenInternal.setTVWindowVideoSource(videoSource)
      const rectangle = [pos.left, pos.top, pos.width, pos.height]
      await this.tizenInternal.showTVWindow(rectangle)
      await this.tuneDirect(source)
      this.channelChangedSubject?.next(true)
    } catch (err: any) {
      this.logService.error(`Could not start broadcast channel`)
      this.channelChangedSubject?.next(false)
    }
  }

  tuneDirect(source: DVBTChannel): Promise<void> {
    return new Promise((resolve, reject) => {
      const tuneOption = {
        broadcastStandard: 'DVB',
        channelType: 'DTV',
        frequency: source.freqHz / 1000,
        modulationType: 'AUTO_QAM',
        bandwidth: `${source.bandwidthMHz}Mhz`,
        programNumber: source.serviceId,
      }
      const successCallback = () => {
        this.logService.debug(`[tuneDirect] success`)
        resolve()
      }
      const errorCallback = (err: any) => {
        this.logService.error(`[tuneDirect] [${err?.code}] ${err?.name}: ${err?.message}`)
        reject()
      }
      this.logService.debug(`[tuneDirect] ${JSON.stringify(tuneOption)}`)
      b2bapis.b2bbroadcast.tuneDirect(tuneOption, successCallback, errorCallback)
    })
  }

  stopUDPChannel(): void {
    try {
      webapis.avplay.stop()
    } catch (err: any) {
      this.logService.error(`Could not stop UDP channel, error = ${err.message}`)
    }
    try {
      webapis.avplay.close()
    } catch (err: any) {
      this.logService.error(`Could not close UDP channel, error = ${err.message}`)
    }
  }

  stopWebChannel(): void {
    try {
      webapis.avplay.stop()
    } catch (err: any) {
      this.logService.error(`Could not stop WEB channel, error = ${err.message}`)
    }
    try {
      webapis.avplay.close()
    } catch (err: any) {
      this.logService.error(`Could not close WEB channel, error = ${err.message}`)
    }
  }

  stopBroadcastChannel(): void {
    this.tizenInternal.hideTVWindow()
  }

  getCurrentChannel(): Promise<any> {
    return new Promise(resolve => {
      let count = 0
      const pollCurrentStreamInfo = () => {
        count++

        try {
          const state = webapis.avplay.getState()
          if (state == 'READY' || state == 'PLAYING' || state == 'PAUSED') {
            const currentStreamInfo = webapis.avplay.getCurrentStreamInfo()
            if (currentStreamInfo.length > 0) {
              return resolve(true)
            }
          }
        } catch (err: any) {
          // do nothing
        }

        if (count > 5) {
          return resolve(false)
        }
        setTimeout(pollCurrentStreamInfo, 500)
      }
      pollCurrentStreamInfo()
    })
  }

  getUDPCurrentProgramData(): Promise<CurrentChannelData | undefined> {
    return this.getAVPlayCurrentProgramData()
  }

  getWebCurrentProgramData(): Promise<CurrentChannelData | undefined> {
    return this.getAVPlayCurrentProgramData()
  }

  getAVPlayCurrentProgramData(): Promise<CurrentChannelData | undefined> {
    const channelData: CurrentChannelData = {
      audioTracks: [],
      subtitleTracks: [],
      isSubtitleEnabled: false,
      currentAudioTrack: undefined,
      currentSubtitleTrack: undefined,
    }

    const mapToAudioTrack = (trackInfo: any, trackExtraInfo: any): ChannelTrackData => {
      const flags: string[] = []
      if (trackExtraInfo.audio_type && trackExtraInfo.audio_type == 3) {
        flags.push(ChannelTrackDataFlags.VISUAL_IMPAIRED)
      }
      return {
        index: trackInfo.index,
        lang: this.tizenInternal.getLang(trackExtraInfo.track_lang),
        data: trackExtraInfo,
        flags: flags,
      }
    }

    const mapToSubtitleTrack = (trackInfo: any, trackExtraInfo: any): ChannelTrackData => {
      const flags: string[] = []
      if (trackExtraInfo.subtitle_mode && trackExtraInfo.subtitle_mode == 1) {
        flags.push(ChannelTrackDataFlags.HEARING_IMPAIRED)
      }
      return {
        index: trackInfo.index,
        lang: this.tizenInternal.getLang(trackExtraInfo.track_lang),
        data: trackExtraInfo,
        flags: flags,
      }
    }

    const isSubtitleEnabled = this.tizenInternal.getSubtitleState()
    this.isSubtitleEnabled = isSubtitleEnabled
    channelData.isSubtitleEnabled = isSubtitleEnabled

    const state = webapis.avplay.getState()
    if (state == 'READY' || state == 'PLAYING' || state == 'PAUSED') {
      const currentStreamInfo = webapis.avplay.getCurrentStreamInfo()
      const totalTrackInfo = webapis.avplay.getTotalTrackInfo()

      if (totalTrackInfo.length == 0) {
        this.logService.debug(`Could not retrieve any tracks`)
        return Promise.resolve(undefined)
      }

      for (let i = 0; i < currentStreamInfo.length; i++) {
        const trackInfo = currentStreamInfo[i]
        const trackExtraInfo = JSON.parse(trackInfo.extra_info)
        switch (trackInfo.type) {
          case 'AUDIO': {
            channelData.currentAudioTrack = mapToAudioTrack(trackInfo, trackExtraInfo)
            break
          }
          case 'TEXT': {
            channelData.currentSubtitleTrack = mapToSubtitleTrack(trackInfo, trackExtraInfo)
            break
          }
          case 'VIDEO':
            break
          default:
            break
        }
      }

      for (let i = 0; i < totalTrackInfo.length; i++) {
        const trackInfo = totalTrackInfo[i]
        const trackExtraInfo = JSON.parse(trackInfo.extra_info)
        switch (trackInfo.type) {
          case 'AUDIO': {
            channelData.audioTracks.push(mapToAudioTrack(trackInfo, trackExtraInfo))
            break
          }
          case 'TEXT': {
            channelData.subtitleTracks.push(mapToSubtitleTrack(trackInfo, trackExtraInfo))
            break
          }
          case 'VIDEO':
            break
          default:
            break
        }
      }

      return Promise.resolve(channelData)
    } else {
      this.logService.debug(`Cannot retrieve tracks because state is invalid (state = ${state})`)
      return Promise.resolve(undefined)
    }
  }

  getBroadcastCurrentProgramData(): Promise<CurrentChannelData | undefined> {
    const channelData: CurrentChannelData = {
      audioTracks: [],
      subtitleTracks: [],
      isSubtitleEnabled: false,
      currentAudioTrack: undefined,
      currentSubtitleTrack: undefined,
    }

    const mapToAudioTrack = (audioInfo: TizenAudioInfo): ChannelTrackData => {
      const flags: string[] = []
      switch (audioInfo.type) {
        case TizenAudioType.HEARING_IMPAIRED:
          flags.push(ChannelTrackDataFlags.HEARING_IMPAIRED)
          break
        case TizenAudioType.VISUAL_IMPAIRED:
          flags.push(ChannelTrackDataFlags.VISUAL_IMPAIRED)
          break
      }
      return {
        index: audioInfo.index,
        lang: this.tizenInternal.getLang(audioInfo.language),
        data: null,
        flags: flags,
      }
    }

    const mapToSubtitleTrack = (subtitleInfo: TizenSubtitleInfo): ChannelTrackData => {
      const flags: string[] = []
      if (subtitleInfo.mode === TizenSubtitleMode.HEARING_IMPAIRED) {
        flags.push(ChannelTrackDataFlags.HEARING_IMPAIRED)
      }
      return {
        index: subtitleInfo.index,
        lang: this.tizenInternal.getLang(subtitleInfo.language),
        data: null,
        flags: flags,
      }
    }

    const isSubtitleEnabled = this.tizenInternal.getSubtitleState()
    this.isSubtitleEnabled = isSubtitleEnabled
    channelData.isSubtitleEnabled = isSubtitleEnabled

    const audios = this.getBroadcastTotalAudio()
    channelData.audioTracks = audios.map(mapToAudioTrack)

    const currentAudio = this.getBroadcastCurrentAudioInfo()
    if (currentAudio) {
      channelData.currentAudioTrack = mapToAudioTrack(currentAudio)
    }

    const subtitles = this.getBroadcastTotalSubtitle()
    channelData.subtitleTracks = subtitles.map(mapToSubtitleTrack)

    const currentSubtitle = this.getBroadcastCurrentSubtitleInfo()
    if (currentSubtitle) {
      channelData.currentSubtitleTrack = mapToSubtitleTrack(currentSubtitle)
    }

    return Promise.resolve(channelData)
  }

  getBroadcastCurrentAudioInfo(): TizenAudioInfo | undefined {
    try {
      return b2bapis.b2bbroadcast.getCurrentAudioInfo()
    } catch (err: any) {
      this.logService.error(`Could not retrieve current audio info, error = ${err.message}`)
      return undefined
    }
  }

  getBroadcastTotalAudio(): TizenAudioInfo[] {
    try {
      return b2bapis.b2bbroadcast.getTotalAudioInfo()
    } catch (err: any) {
      this.logService.error(`Could not retrieve total audio info, error = ${err.message}`)
      return []
    }
  }

  getBroadcastCurrentSubtitleInfo(): TizenSubtitleInfo | undefined {
    try {
      return b2bapis.b2bbroadcast.getCurrentSubtitle()
    } catch (err: any) {
      this.logService.error(`Could not retrieve current subtitle info, error = ${err.message}`)
      return undefined
    }
  }

  getBroadcastTotalSubtitle(): TizenSubtitleInfo[] {
    try {
      return b2bapis.b2bbroadcast.getTotalSubtitleInfo()
    } catch (err: any) {
      this.logService.error(`Could not retrieve total subtitle info, error = ${err.message}`)
      return []
    }
  }

  setUDPAudioTrack(track: ChannelTrackData): void {
    this.setAVPlayAudioTrack(track)
  }

  setWebAudioTrack(track: ChannelTrackData): void {
    this.setAVPlayAudioTrack(track)
  }

  setAVPlayAudioTrack(track: ChannelTrackData): void {
    try {
      webapis.avplay.setSelectTrack('AUDIO', track.index)
    } catch (err: any) {
      this.logService.error(`Could not set audio track ${track.index}, error = ${err.message}`)
    }
  }

  setBroadcastAudioTrack(track: ChannelTrackData): void {
    const successCallback = () => {
      this.logService.debug(`[setCurrentAudio] Successfully set audio track`)
    }
    const errorCallback = (err: any) => {
      this.logService.error(`[setCurrentAudio] Could not set audio track ${track.index}, error = ${err?.message}`)
    }
    try {
      b2bapis.b2bbroadcast.setCurrentAudio(track.index, successCallback, errorCallback)
    } catch (err: any) {
      this.logService.error(`[setCurrentAudio] Could not set audio track ${track.index}, error = ${err?.message}`)
    }
  }

  setUDPSubtitleTrack(track: ChannelTrackData): void {
    this.setAVPlaySubtitleTrack(track)
  }

  setWebSubtitleTrack(track: ChannelTrackData): void {
    this.setAVPlaySubtitleTrack(track)
  }

  setAVPlaySubtitleTrack(track: ChannelTrackData): void {
    try {
      webapis.avplay.setSelectTrack('TEXT', track.index)
    } catch (err: any) {
      this.logService.error(`Could not set subtitle track ${track.index}, error = ${err.message}`)
    }
  }

  setBroadcastSubtitleTrack(track: ChannelTrackData): void {
    const successCallback = () => {
      this.logService.debug(`[setCurrentSubtitleIndex] Successfully set subtitle track`)
    }
    const errorCallback = (err: any) => {
      this.logService.error(
        `[setCurrentSubtitleIndex] Could not set subtitle track ${track.index}, error = ${err.message}`
      )
    }
    try {
      b2bapis.b2bbroadcast.setCurrentSubtitleIndex(track.index, successCallback, errorCallback)
    } catch (err: any) {
      this.logService.error(
        `[setCurrentSubtitleIndex] Could not set subtitle track ${track.index}, error = ${err.message}`
      )
    }
  }

  setSubtitleState(enable: boolean): Promise<boolean> {
    const value = enable ? webapis.tvinfo.TvInfoMenuValue.ON : webapis.tvinfo.TvInfoMenuValue.OFF
    return new Promise((resolve, reject) => {
      const successCallback = () => {
        this.isSubtitleEnabled = enable
        resolve(enable)
      }
      const errorCallback = (err: any) => {
        this.logService.error(`Could not set subtitle state, error = ${err.message}`)
        reject()
      }
      try {
        const key = webapis.tvinfo.TvInfoMenuKey.SUBTITLE_ONOFF_KEY
        webapis.tvinfo.setMenuValue(key, value, successCallback, errorCallback)
      } catch (err: any) {
        this.logService.error(`Could not set subtitle state, error = ${err.message}`)
        reject()
      }
    })
  }

  getLocalMedias(): Promise<LocalMediaFile[]> {
    return new Promise(resolve => {
      tizen.filesystem.resolve(
        this.MEDIA_PATH,
        (dir: { listFiles: (arg0: (files: any) => void, arg1: (err: any) => void) => void }) => {
          dir.listFiles(
            files => {
              const localMedias: LocalMediaFile[] = []

              files.forEach((file: any) => {
                if (!file.isDirectory) {
                  localMedias.push({
                    id: file.name.substring(0, file.name.lastIndexOf('.')) || file.name,
                    filename: file.name,
                    url: file.toURI(),
                    sizeInBytes: file.fileSize,
                    sha1: undefined,
                    md5: undefined,
                  })
                }
              })

              resolve(localMedias)
            },
            err => {
              this.logService.error(`Failed to list file in directory : ${this.MEDIA_PATH}`)
              this.logService.error(`${err.name} [${err.code}] ${err.message}`)
              resolve([])
            }
          )
        },
        (err: any) => {
          this.logService.error(`Failed to open directory : ${this.MEDIA_PATH}`)
          this.logService.error(`${err.name} [${err.code}] ${err.message}`)
          resolve([])
        },
        'r'
      )
    })
  }

  downloadMedia(media: Media): Promise<LocalMediaFile> {
    const filename = `${media.id}.${media.fileExtension}`
    const path = `${this.MEDIA_PATH}/${filename}`

    return new Promise((resolve, reject) => {
      this.pathExists(path, exists => {
        if (exists) {
          resolve({
            id: media.id,
            filename: filename,
            url: path,
            sizeInBytes: undefined,
            sha1: undefined,
            md5: undefined,
          })
        } else {
          const src = `${media.url}?ts=${new Date().getTime()}`
          const downloadRequest = new tizen.DownloadRequest(src, this.MEDIA_PATH, filename)

          const listener = {
            onprogress: (id: string, receivedSize: number, totalSize: number) => {
              this.logService.debug(`Download ${id} progress ${receivedSize}/${totalSize}`)
            },
            onpaused: (id: string) => {
              this.logService.info(`Download ${id} has been paused`)
            },
            oncanceled: (id: string) => {
              this.logService.error(`Download ${id} has been canceled`)
            },
            oncompleted: (id: string, fullPath: string) => {
              this.logService.info(`Download ${id} completed, full path: ${fullPath}`)

              tizen.filesystem.resolve(
                fullPath,
                (file: any) => {
                  resolve({
                    id: media.id,
                    filename: filename,
                    url: file.toURI(),
                    sizeInBytes: file.fileSize,
                    sha1: undefined,
                    md5: undefined,
                  })
                },
                (err: any) => {
                  this.logService.error(`Failed to list files: ${err.name} [${err.code}] ${err.message}`)
                  reject(new Error('Failed to list downloaded files.'))
                }
              )
            },
            onfailed: (id: string, error: any) => {
              this.logService.error(`Download ${id} failed, error: ${error}`)
              reject(new Error(`Failed to download media: ${error}`))
            },
          }

          try {
            const downloadId = tizen.download.start(downloadRequest, listener)
            this.logService.info(`Downloading ${src} (id: ${downloadId})`)
          } catch (error) {
            this.logService.error(`Error while starting download: ${error}`)
            reject(new Error('Error while initiating download process.'))
          }
        }
      })
    })
  }

  removeMedia(mediaFile: LocalMediaFile): Promise<void> {
    const path = `${this.MEDIA_PATH}/${mediaFile.filename}`
    return new Promise((resolve, reject) =>
      tizen.filesystem.resolve(
        this.MEDIA_PATH,
        (dir: any) => {
          dir.deleteFile(
            path,
            () => {
              this.logService.info(`Successfully removed file at ${path}`)
              resolve()
            },
            (error: any) => {
              this.logService.error(`Could not remove file ${path}, error = ${JSON.stringify(error)}`)
              reject()
            }
          )
        },
        (error: any) => {
          this.logService.error(`Could not resolve directory ${this.MEDIA_PATH}, error = ${JSON.stringify(error)}`)
          reject()
        },
        'rw'
      )
    )
  }

  private pathExists(path: string, callback: (exists: boolean) => void): void {
    try {
      tizen.filesystem.resolve(
        path,
        () => callback(true),
        () => callback(false),
        'r'
      )
    } catch (error) {
      callback(false)
    }
  }

  setNativeVolumeDisplay(enabled: boolean): void {
    const keys = ['VolumeUp', 'VolumeDown', 'VolumeMute']
    if (enabled) {
      keys.forEach(key => {
        try {
          this.logService.debug(`Unregistering Key ${key}`)
          tizen.tvinputdevice.unregisterKey(key)
        } catch (err: any) {
          this.logService.error(`Could not unregister key ${key}, error = ${err?.message}`)
        }
      })
    } else {
      keys.forEach(key => {
        try {
          this.logService.debug(`Registering Key ${key}`)
          tizen.tvinputdevice.registerKey(key)
        } catch (err: any) {
          this.logService.error(`Could not register key ${key}, error = ${err?.message}`)
        }
      })
    }
  }

  getVolume(): Promise<number> {
    return Promise.resolve(tizen.tvaudiocontrol.getVolume())
  }

  setVolume(volume: number): Promise<number> {
    try {
      tizen.tvaudiocontrol.setVolume(volume)
      return Promise.resolve(tizen.tvaudiocontrol.getVolume())
    } catch (err: any) {
      this.logService.error(`Could not set volume, error = ${err.message}`)
      return Promise.resolve(0)
    }
  }

  setVolumeDown(): Promise<number> {
    try {
      tizen.tvaudiocontrol.setVolumeDown()
      return Promise.resolve(tizen.tvaudiocontrol.getVolume())
    } catch (err: any) {
      this.logService.error(`Could not set volume up, error = ${err.message}`)
      return Promise.resolve(0)
    }
  }

  setVolumeUp(): Promise<number> {
    try {
      tizen.tvaudiocontrol.setVolumeUp()
      return Promise.resolve(tizen.tvaudiocontrol.getVolume())
    } catch (err: any) {
      this.logService.error(`Could not set volume up, error = ${err.message}`)
      return Promise.resolve(0)
    }
  }

  getVolumeMute(): Promise<boolean> {
    return Promise.resolve(tizen.tvaudiocontrol.isMute())
  }

  setVolumeMute(muted: boolean): Promise<boolean> {
    try {
      tizen.tvaudiocontrol.setMute(muted)
      return Promise.resolve(tizen.tvaudiocontrol.isMute())
    } catch (err: any) {
      this.logService.error(`Could not change mute status, error = ${err.message}`)
      return Promise.resolve(false)
    }
  }

  getExternalInputs(): Promise<ExternalInputData[]> {
    return this.tizenInternal.getExternalInputs()
  }

  // Disabled since it is causing TV crash when launching the HDMI with the listener registered
  // Disabling the listener before launching the HDMI source did not help with the crash
  // So for Samsung Tizen, we only check the HDMI ports status during startup
  registerExternalInputListener(subject: BehaviorSubject<ExternalInputData[]>): void {
    this.tizenInternal.registerExternalInputListener(subject)
  }

  async showHDMI(externalInput: ExternalInputData): Promise<void> {
    try {
      const videoSource = await this.tizenInternal.getVideoSource('HDMI', externalInput.number)
      await this.tizenInternal.setTVWindowVideoSource(videoSource)
      await this.tizenInternal.showTVWindow(['0', '0', '100%', '100%'])
    } catch (err: any) {
      this.logService.error(`Could not show HDMI source, error = ${err?.message}`)
    }
  }

  hideHDMI(): Promise<void> {
    this.tizenInternal.hideTVWindow()
    return Promise.resolve()
  }

  setupURLLauncherAddress(deviceId: string | undefined): void {
    if (environment.localEnv) {
      return
    }

    let urlLauncherAddress: string
    let platformVersion: string

    try {
      urlLauncherAddress = b2bapis.b2bcontrol.getURLLauncherAddress()
    } catch (err: any) {
      this.logService.error(`Could not get URLLauncherAddress, error = ${err?.message}`)
      return
    }

    try {
      platformVersion = tizen.systeminfo.getCapability('http://tizen.org/feature/platform.version')
    } catch (err: any) {
      this.logService.error(`Could not retrieve Tizen platform version, error = ${err?.message}`)
      return
    }

    let baseAppUrl: string
    if (urlLauncherAddress.startsWith('http://')) {
      baseAppUrl = `http://${environment.appDomain}/hbrowser`
    } else {
      baseAppUrl = `https://${environment.appDomain}/htv/tizen/${platformVersion}`
    }

    const downloadServerAddress = deviceId ? `${baseAppUrl}/${deviceId}` : baseAppUrl

    if (urlLauncherAddress != downloadServerAddress) {
      try {
        b2bapis.b2bcontrol.setURLLauncherAddress(
          downloadServerAddress,
          () => {
            this.logService.info(`Successfully configured launcher address to ${downloadServerAddress}`)
          },
          (err: any) => {
            this.logService.error(`An error occurred during setURLLauncherAddress, error = ${err?.message}`)
          }
        )
      } catch (err: any) {
        this.logService.error(`Could not set URLLauncherAddress, error = ${err?.message}`)
      }
    }
  }

  hasIntegratedGoogleCast(): boolean {
    return false
  }

  initGoogleCastApp(): void {
    this.gcInitializedSubject?.next(true)
  }

  registerGoogleCastInitializedListener(subject: BehaviorSubject<boolean | undefined>): void {
    this.gcInitializedSubject = subject
  }

  disableGoogleCast(): Promise<void> {
    return Promise.resolve()
  }

  initGoogleCastProxy(): Promise<void> {
    return Promise.resolve()
  }

  initGoogleCastSoftAP(): Promise<void> {
    return Promise.resolve()
  }

  acceptGoogleCastTermsOfService(): Promise<void> {
    return Promise.resolve()
  }

  enableGoogleCastUsageReporting(): Promise<void> {
    return Promise.resolve()
  }

  clearGoogleCastData(): Promise<void> {
    return Promise.resolve()
  }

  openGoogleCastLocalPage(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    roomNumber: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    supportMessage: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    gcStateSubject: BehaviorSubject<GoogleCastState | undefined>
  ): Promise<void> {
    return Promise.resolve()
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  openGoogleCastSoftAPPage(roomNumber: string, pairingStatus: boolean, supportMessage: string): Promise<void> {
    return Promise.resolve()
  }

  pollGoogleCastSoftAP(): Promise<any> {
    const softAPSSID = this.tizenInternal.getSoftAPSSID()
    const softAPSecurityKey = this.tizenInternal.getSoftAPSecurityKey()
    return Promise.resolve({
      ssid: softAPSSID,
      password: softAPSecurityKey,
    })
  }

  launchGoogleCastSettings(): Promise<void> {
    return Promise.resolve()
  }

  adminOpenAppsSettings(): void {
    this.startApp('com.samsung.tv.store')
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  downloadAndUpgradeApp(deviceId: string): void {
    // do nothing
  }

  canDownloadAndUpgradeApp(): boolean {
    return false
  }

  // Using Shaka player for now since it's easier to maintain only one accross various platforms
  hasCustomVOD(): boolean {
    return false
  }

  getShakaPlatformConfig(): any {
    const model = this.tizenInternal.getModel()
    switch (model) {
      case 'HF690':
        return {
          abr: {
            restrictions: {
              maxHeight: 1080,
              maxBandwidth: 8000000, // 8 Mbps max
            },
          },
          preferredVideoCodecs: ['avc1', 'vp8', 'vp09'], // The list of preferred video codecs, in order of highest to lowest priority.
        }
      default:
        return undefined
    }
  }

  vodStart(vod: VODVideo, pos: ChannelPosition): void {
    let drmSessionId: string | undefined
    const drmAppSession = String(Math.floor(Math.random() * Math.floor(10000)))

    const listener = {
      onbufferingstart: () => {
        this.logService.debug('Buffering start')
      },
      onbufferingprogress: (percent: any) => {
        this.logService.debug('Buffering progress: ' + percent + '%')
      },
      onbufferingcomplete: () => {
        this.logService.debug('Buffering complete')
      },
      onstreamcompleted: () => {
        this.logService.debug('Stream completed')
        vod.state?.status$.next(VODStatus.COMPLETED)
        webapis.avplay.stop()
      },
      oncurrentplaytime: (currentTime: any) => {
        vod.state?.currentTime$.next(currentTime)
      },
      onerror: (eventType: any) => {
        this.logService.error('(onerror) ' + eventType)
      },
      onerrormsg: (eventType: any, errorMsg: any) => {
        this.logService.debug(`(onerrormsg) ${eventType} ${errorMsg}`)
      },
      onevent: (eventType: any, eventData: any) => {
        this.logService.debug(`(onevent) ${eventType} data: ${JSON.stringify(eventData)}`)
      },
      onsubtitlechange: (duration: any, text: any, type: string, attributes: any) => {
        this.logService.debug(`(onsubtitlechange) ${duration} ${text} ${type} ${JSON.stringify(attributes)}`)
        vod.state?.subtitleChange$.next({ duration, text })
      },
      ondrmevent: (drmEvent: any, drmData: any) => {
        try {
          this.logService.debug(`(ondrmevent) ${drmEvent} data: ${JSON.stringify(drmData)}`)

          if (drmData.name === 'Challenge' && vod.drm && vod.drmConfig && vod.drmConfig.widevineLicenseUrl) {
            drmSessionId = drmData.session_id

            this.tizenInternal.widevineDrmChallenge(vod.drmConfig.widevineLicenseUrl, drmData.challenge).subscribe({
              next: (license: string) => {
                webapis.avplay.setDrm(
                  'WIDEVINE_CDM',
                  'widevine_license_data',
                  drmSessionId + 'PARAM_START_POSITION' + btoa(license) + 'PARAM_START_POSITION'
                )
              },
              error: () => {
                this.logService.debug(`Could not retrieve drm license from Widevine`)
              },
            })
          } else if (drmData.name === 'DrmError') {
            this.logService.error(`(ondrmevent) DRM Error: [${drmData.code}] ${drmData.message}`)
          }
        } catch (err: any) {
          this.logService.error(`(ondrmevent) Could not handle data`)
        }
      },
    }

    this.logService.debug(`Loading VOD ${vod.id}`)

    vod.state?.status$.next(VODStatus.LOADING)
    vod.state?.duration$.next(undefined)
    vod.state?.currentTime$.next(undefined)

    webapis.avplay.open(vod.videoUrl)
    webapis.avplay.setDisplayRect(pos.left, pos.top, pos.width, pos.height)
    webapis.avplay.setListener(listener)

    if (vod.drm) {
      webapis.avplay.setDrm('WIDEVINE_CDM', 'Initialize', '')
      webapis.avplay.setDrm('WIDEVINE_CDM', 'widevine_app_session', drmAppSession)
      webapis.avplay.setDrm('WIDEVINE_CDM', 'widevine_data_type', 'MPEG-DASH')
    }

    webapis.avplay.prepareAsync(
      () => {
        this.logService.debug(`VOD ${vod.id} loaded`)
        this.setSubtitleState(false)

        vod.state?.status$.next(VODStatus.READY)
        vod.state?.duration$.next(webapis.avplay.getDuration())
        vod.state?.currentTime$.next(webapis.avplay.getCurrentTime())
      },
      (err: any) => {
        this.logService.error(`Failed to prepare AVPlay for URL: ${vod.videoUrl}, error = ${err?.message}`)
      }
    )
  }

  vodPlay(vod: VODVideo): void {
    try {
      const state = webapis.avplay.getState()
      if (state == 'IDLE') {
        vod.state?.status$.next(VODStatus.LOADING)
        vod.state?.duration$.next(undefined)
        vod.state?.currentTime$.next(undefined)

        webapis.avplay.prepareAsync(
          () => {
            this.logService.debug(`VOD ${vod.id} loaded`)

            vod.state?.status$.next(VODStatus.READY)
            vod.state?.duration$.next(webapis.avplay.getDuration())
            vod.state?.currentTime$.next(webapis.avplay.getCurrentTime())

            webapis.avplay.play()
            vod.state?.status$.next(VODStatus.PLAYING)
          },
          (err: any) => {
            this.logService.error(`Failed to prepare AVPlay for URL: ${vod.videoUrl}, error = ${err?.message}`)
          }
        )
      } else {
        webapis.avplay.play()
        vod.state?.status$.next(VODStatus.PLAYING)
      }
    } catch (err: any) {
      this.logService.error(`(webapis.avplay.play) error = ${err?.message}`)
    }
  }

  vodPause(vod: VODVideo): void {
    try {
      webapis.avplay.pause()
      vod.state?.status$.next(VODStatus.PAUSED)
    } catch (err: any) {
      this.logService.error(`(webapis.avplay.pause) error = ${err?.message}`)
    }
  }

  vodStop(): void {
    try {
      webapis.avplay.stop()
    } catch (err: any) {
      this.logService.error(`(webapis.avplay.stop) error = ${err?.message}`)
    }
    try {
      webapis.avplay.close()
    } catch (err: any) {
      this.logService.error(`(webapis.avplay.close) error = ${err.message}`)
    }
  }

  vodGetCurrentTime(): number {
    try {
      return webapis.avplay.getCurrentTime()
    } catch (err: any) {
      this.logService.error(`(webapis.avplay.getCurrentTime) error = ${err?.message}`)
      return 0
    }
  }

  vodSpeed(vod: VODVideo, speed: number): void {
    try {
      const state = webapis.avplay.getState()
      if (state == 'READY' || state == 'PLAYING' || state == 'PAUSED') {
        if (speed != 1) {
          this.vodPlay(vod)
        }
        webapis.avplay.setSpeed(speed)
      } else {
        this.logService.debug(`(webapis.avplay.setSpeed) invalid state = ${state}`)
      }
    } catch (err: any) {
      this.logService.error(`(webapis.avplay.setSpeed) error = ${err?.message}`)
    }
  }

  vodSeekTo(vod: VODVideo, milliseconds: number): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.isSeeking) {
        this.isSeeking = true
        try {
          const state = webapis.avplay.getState()
          if (state == 'IDLE' || state == 'READY' || state == 'PLAYING' || state == 'PAUSED') {
            webapis.avplay.seekTo(milliseconds)
            this.logService.debug('(webapis.avplay.seekTo) success')
            vod.state?.currentTime$.next(webapis.avplay.getCurrentTime())
            this.isSeeking = false
            resolve()
          } else {
            this.logService.debug(`(webapis.avplay.seekTo) invalid state = ${state}`)
            this.isSeeking = false
            reject()
          }
        } catch (err: any) {
          this.logService.error(`(webapis.avplay.seekTo) error = ${err?.message}`)
          this.isSeeking = false
          reject()
        }
      } else {
        this.logService.debug(`(webapis.avplay.seekTo) Already seeking, aborting seekTo(${milliseconds})`)
        resolve()
      }
    })
  }

  vodJumpBackward(vod: VODVideo, milliseconds: number): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.isSeeking) {
        this.isSeeking = true
        try {
          const state = webapis.avplay.getState()
          if (state == 'READY' || state == 'PLAYING' || state == 'PAUSED') {
            webapis.avplay.jumpBackward(milliseconds)
            this.logService.debug('(webapis.avplay.jumpBackward) success')
            vod.state?.currentTime$.next(webapis.avplay.getCurrentTime())
            this.isSeeking = false
            resolve()
          } else {
            this.logService.debug(`(webapis.avplay.jumpBackward) invalid state = ${state}`)
            this.isSeeking = false
            reject()
          }
        } catch (err: any) {
          this.logService.error(`(webapis.avplay.jumpBackward) error = ${err?.message}`)
          this.isSeeking = false
          reject()
        }
      } else {
        this.logService.debug(`(webapis.avplay.jumpBackward) Already seeking, aborting seekTo(${milliseconds})`)
        resolve()
      }
    })
  }

  vodJumpForward(vod: VODVideo, milliseconds: number): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.isSeeking) {
        this.isSeeking = true
        try {
          const state = webapis.avplay.getState()
          if (state == 'READY' || state == 'PLAYING' || state == 'PAUSED') {
            webapis.avplay.jumpForward(milliseconds)
            this.logService.debug('(webapis.avplay.jumpForward) success')
            vod.state?.currentTime$.next(webapis.avplay.getCurrentTime())
            this.isSeeking = false
            resolve()
          } else {
            this.logService.debug(`(webapis.avplay.jumpForward) invalid state = ${state}`)
            this.isSeeking = false
            reject()
          }
        } catch (err: any) {
          this.logService.error(`(webapis.avplay.jumpForward) error = ${err?.message}`)
          this.isSeeking = false
          reject()
        }
      } else {
        this.logService.debug(`(webapis.avplay.jumpForward) Already seeking, aborting seekTo(${milliseconds})`)
        resolve()
      }
    })
  }

  initVirtualKeyboard(): void {
    return
  }
}
